import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { DEVICE_TYPE_OPTIONS } from '../../../utils/deviceTypes';

const DEFAULT_EXPIRY_DAYS = 30;

const REQUEST_TYPES = {
  tailscale_approval: {
    label: 'Tailscale Approval',
    description: 'Request Tailscale approval for this device',
    showPI: false,
    showExpiryDate: true,
    showReason: false,
    reasonLabel: 'Notes',
    reasonPlaceholder: 'Add any additional notes or comments...',
  },
  registration_renewal: {
    label: 'Registration Renewal',
    description: 'Renew the device registration',
    showPI: true,
    showExpiryDate: true,
    showReason: false,
    reasonLabel: 'Notes',
    reasonPlaceholder: 'Add any additional notes or comments...',
  },
  device_lost: {
    label: 'Report Lost Device',
    description: 'Report this device as lost or missing',
    showPI: false,
    showExpiryDate: false,
    showReason: true,
    reasonLabel: 'Loss Details',
    reasonPlaceholder: 'Describe where and when the device was lost...',
    reasonRequired: true,
  },
  device_replacement: {
    label: 'Request Replacement',
    description: 'Request a replacement for a broken or damaged device',
    showPI: false,
    showExpiryDate: false,
    showReason: true,
    reasonLabel: 'Damage Details',
    reasonPlaceholder: 'Describe the damage or issue with the device...',
    reasonRequired: true,
  },
  device_return: {
    label: 'Request Return',
    description: 'Return this device (no longer needed)',
    showPI: false,
    showExpiryDate: false,
    showReason: true,
    reasonLabel: 'Return Reason',
    reasonPlaceholder: 'Explain why you are returning this device...',
    reasonRequired: false,
  },
};

const RegistrationRequestDialog = ({
  open,
  onClose,
  device,
  registrationType,
  onSubmit,
  isLoading = false,
  allowTypeSelection = false,
  isAdmin = false,
}) => {
  const [selectedType, setSelectedType] = useState(registrationType || 'tailscale_approval');
  const [pi, setPi] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [expiryDate, setExpiryDate] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedType(registrationType || 'tailscale_approval');
      setPi('');
      setDeviceType('');
      setNotes('');
      setReason('');
      setExpiryDate(dayjs().add(DEFAULT_EXPIRY_DAYS, 'day'));
      setError('');
    }
  }, [open, registrationType]);

  const currentConfig = REQUEST_TYPES[selectedType] || REQUEST_TYPES.tailscale_approval;

  const isDeviceLost = device?.is_lost === true;
  const isDeviceBroken = device?.is_broken === true;
  const isDeviceUnavailable = isDeviceLost || isDeviceBroken;

  const handleSubmit = () => {
    setError('');

    if (isDeviceUnavailable) {
      setError(isDeviceLost 
        ? 'Cannot create requests for a lost device. Please contact support if you need assistance.'
        : 'Cannot create requests for a broken device. Please contact support if you need assistance.');
      return;
    }

    if (currentConfig.reasonRequired && !reason.trim()) {
      setError(`${currentConfig.reasonLabel} is required`);
      return;
    }

    if (currentConfig.showPI && pi === 'other' && !deviceType) {
      setError('Please select the device type');
      return;
    }

    let finalExpiryDate = null;
    if (currentConfig.showExpiryDate) {
      if (isAdmin && expiryDate) {
        finalExpiryDate = expiryDate.toISOString();
      } else {
        finalExpiryDate = dayjs().add(DEFAULT_EXPIRY_DAYS, 'day').toISOString();
      }
    }

    const requestData = {
      registration_type: selectedType,
      ...(currentConfig.showPI && pi && { pi }),
      ...(currentConfig.showPI && pi === 'other' && deviceType && { device_type: deviceType }),
      ...(finalExpiryDate && { expiry_date: finalExpiryDate }),
      ...(currentConfig.showReason && reason.trim() && { reason: reason.trim() }),
      ...(notes.trim() && { notes: notes.trim() }),
    };

    onSubmit(requestData);
    
    setPi('');
    setDeviceType('');
    setNotes('');
    setReason('');
    setExpiryDate(dayjs().add(DEFAULT_EXPIRY_DAYS, 'day'));
    setError('');
  };

  const handleClose = () => {
    setError('');
    setPi('');
    setDeviceType('');
    setNotes('');
    setReason('');
    setExpiryDate(dayjs().add(DEFAULT_EXPIRY_DAYS, 'day'));
    onClose();
  };

  const getTitle = () => {
    if (allowTypeSelection) {
      return 'Device Request';
    }
    return currentConfig.label || 'Device Request';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent>
        {device && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <Alert severity="info">
              Device: {device.mac_addr}
              {device.user && ` (Current Owner: ${device.user.display_name || device.user.email})`}
            </Alert>
          </Box>
        )}

        {isDeviceUnavailable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {isDeviceLost 
              ? 'This device is marked as LOST. You cannot create any requests for this device. Please contact support if you need assistance.'
              : 'This device is marked as BROKEN. You cannot create any requests for this device. Please contact support if you need assistance.'}
          </Alert>
        )}
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {allowTypeSelection && (
          <FormControl fullWidth sx={{ mb: 2, mt: device ? 0 : 1 }}>
            <InputLabel>Request Type</InputLabel>
            <Select
              value={selectedType}
              label="Request Type"
              onChange={(e) => {
                setSelectedType(e.target.value);
                setPi('');
                setDeviceType('');
                setReason('');
                setExpiryDate(dayjs().add(DEFAULT_EXPIRY_DAYS, 'day'));
              }}
              disabled={isLoading || isDeviceUnavailable}
            >
              {Object.entries(REQUEST_TYPES).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  {config.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {currentConfig.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {currentConfig.description}
          </Typography>
        )}

        {currentConfig.showPI && (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>PI</InputLabel>
              <Select
                value={pi}
                label="PI"
                onChange={(e) => {
                  setPi(e.target.value);
                  if (e.target.value !== 'other') {
                    setDeviceType('');
                  }
                }}
                disabled={isLoading || isDeviceUnavailable}
              >
                <MenuItem value="">None (Optional)</MenuItem>
                <MenuItem value="pi-zero">PI - Zero</MenuItem>
                <MenuItem value="pi-4">PI - 4b</MenuItem>
                <MenuItem value="cm5">CM5</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            {pi === 'other' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Device Type</InputLabel>
                <Select
                  value={deviceType}
                  label="Device Type"
                  onChange={(e) => setDeviceType(e.target.value)}
                  disabled={isLoading || isDeviceUnavailable}
                  required
                >
                  {DEVICE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        )}

        {currentConfig.showExpiryDate && isAdmin && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Expiry Date"
              value={expiryDate}
              onChange={(newValue) => setExpiryDate(newValue)}
              disabled={isLoading || isDeviceUnavailable}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { mb: 2 },
                  helperText: `Default: 30 days from approval`,
                },
              }}
              minDate={dayjs()}
            />
          </LocalizationProvider>
        )}

        {currentConfig.showExpiryDate && !isAdmin && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Registration will expire in {DEFAULT_EXPIRY_DAYS} days after approval.
          </Alert>
        )}

        {currentConfig.showReason && (
          <TextField
            label={currentConfig.reasonLabel + (currentConfig.reasonRequired ? ' *' : ' (Optional)')}
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={currentConfig.reasonPlaceholder}
            disabled={isLoading || isDeviceUnavailable}
            required={currentConfig.reasonRequired}
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          label="Additional Notes (Optional)"
          fullWidth
          multiline
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any additional notes or comments..."
          disabled={isLoading || isDeviceUnavailable}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || isDeviceUnavailable || (currentConfig.reasonRequired && !reason.trim())}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          Submit Request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrationRequestDialog;

export { REQUEST_TYPES, DEFAULT_EXPIRY_DAYS };
